import React, {
    ReactElement, useState,
} from "react";
import { BiEditAlt } from "react-icons/bi";
import { BsBookmarkCheck } from "react-icons/bs";
import { FaPlus } from "react-icons/fa";
import { RiArticleLine } from "react-icons/ri";
import TimeAgo from "react-timeago";
import {
    Permission, useListArticlesQuery,
} from "../../../__generated__/api";
import { Loading, SearchBar } from "../../../components";
import Wave from "../../../components/wave/Wave";
import usePortal from "../../../contexts/PortalContext";
import useRouting, { Link, Navigate } from "../../../contexts/Routing";
import { NoDataOrError } from "../../error/Error";
import "./AnswerList.scss";

export default function AnswerList(): ReactElement {
    const { welcomeMessage, permissions, indexArticleId } = usePortal();

    const { Paths } = useRouting();

    const [searchTerm, setSearchTerm] = useState<string>("");
    const { error, loading, data } = useListArticlesQuery({
        variables: {
            query: searchTerm,
        },
    });

    const articles = data?.articles?.results;
    if (error) {
        return <NoDataOrError error={error} />;
    }

    if (!permissions.has(Permission.ArticleReadPublic)) {
        return <Navigate to={Paths.LOGIN} search={{ redirect: window.location.href }} />;
    }

    if (indexArticleId) {
        return <Navigate to={Paths.ANSWERS_GET} params={{ id: indexArticleId }} />;
    }

    return (
        <div className="h-100">
            <Wave type="lg" wave="bottom">
                <div className="w-100 container">
                    <h2 className="w-100 text-center text-light mb-5">
                        {welcomeMessage}
                    </h2>
                    <SearchBar
                        onChange={setSearchTerm}
                        className="form-control shadow-lg fs-4 py-2"
                        isLoading={searchTerm !== "" && loading}
                    />
                </div>
            </Wave>
            {!searchTerm && loading && (
                <Loading />
            )}

            <div data-test="answer-list" className="container">
                <div className="w-100 mt-4">

                    {articles?.isEmpty ? (
                        <div className="text-center empty-answers-list">
                            {searchTerm ? (
                                <h3>No articles match your search.</h3>
                            ) : (
                                <div>
                                    <h3>Empty Knowledge base.</h3>
                                    {permissions.has(Permission.ArticleModify) && (
                                        <>
                                            <p>Would you like to create an article?</p>
                                            <Link
                                                to={Paths.ANSWERS_NEW}
                                                className="btn btn-primary btn-lg"
                                            >
                                                Create an Article
                                            </Link>
                                        </>
                                    )}
                                </div>
                            )}

                        </div>
                    ) : (
                        <section>
                            <div className="list-group d-flex flex-row flex-wrap align-items-top
                                             justify-content-xl-between justify-content-center answers-list"
                            >

                                {articles?.dedupe(article => article.id).map(article => {
                                    const publishedDate = article.publishedDate
                                        ? new Date(article.publishedDate)
                                        : undefined;
                                    return (
                                        <Link
                                            data-test="article-card"
                                            key={article.id}
                                            to={Paths.ANSWERS_GET}
                                            params={{
                                                id: article.id,
                                            }}
                                            className={"list-group-item list-group-item-action flex-row answer-list-item "
                                                    + "align-items-start p-4 mb-4 rounded d-flex align-items-top half-card"}
                                        >
                                            {article.iconUrl ? (
                                                <img
                                                    src={article.iconUrl}
                                                    alt="Article icon"
                                                    className="icon-image mx-3"
                                                />
                                            ) : (
                                                <RiArticleLine className="mx-3 icon-image text-light-300" />
                                            )}

                                            <div className="ms-3 w-100">
                                                <div className="mb-3">
                                                    <h5 className="mb-1">{article.title}</h5>
                                                    <small className="text-light-400 card-comment">
                                                        {article.creator?.name && (
                                                            <span>
                                                                {article.creator?.name}
                                                                {" "}
                                                                |
                                                                {" "}
                                                            </span>
                                                        )}

                                                        <TimeAgo date={article.modifiedDate} />
                                                        {" "}
                                                        |
                                                        {" "}
                                                        {(publishedDate
                                                         && publishedDate.getTime() < Date.now())
                                                            ? (

                                                                <BsBookmarkCheck
                                                                    title={`Published ${publishedDate?.toLocaleString()
                                                                    } by ${article?.publishedBy?.name}`}
                                                                />

                                                            ) : (
                                                                <BiEditAlt title="Answer is unpublished" />
                                                            )}
                                                    </small>
                                                </div>
                                                {article.previewText ? (

                                                    <p
                                                        className="mb-4 mt-1 text-dark"
                                                        /* eslint-disable-next-line react/no-danger */
                                                        dangerouslySetInnerHTML={{ __html: article.previewText ?? "" }}
                                                    />
                                                ) : (
                                                    <p className="mb-4 mt-1 text-dark">
                                                        {(article.body.plaintext as string)
                                                            .createBlurbSingleLine(200)}
                                                    </p>
                                                )}

                                            </div>

                                        </Link>

                                    );
                                })}

                                {permissions.has(Permission.ArticleModify) && (
                                    <Link
                                        to={Paths.ANSWERS_NEW}
                                        className={"list-group-item list-group-item-action flex-row answer-list-item "
                                                + "align-items-center justify-content-center p-4 mb-4 rounded d-flex "
                                                + "half-card fs-5 text-title"}
                                    >
                                        <FaPlus className="me-3" />
                                        Create
                                    </Link>
                                )}

                            </div>
                        </section>

                    )}
                </div>
            </div>

        </div>
    );
}
